// Tab-side client for the per-origin query-permit SharedWorker.
//
// The SharedWorker (worker.ts) holds a global semaphore that caps the
// number of in-flight /api/query/* requests across all tabs in this origin. Tabs call
// acquireQueryPermit() before fetching and invoke the returned release thunk in a finally.
//
// When SharedWorker is unavailable (mobile web, Chrome Android, some embedded webviews) the
// broker falls back to a tab-local permit pool that still enforces the per-project cap
// within this tab — it just can't coordinate across tabs (see createLocalPermitClient).
//
// Which requests need a permit is decided by the caller, not here: api-client calls
// acquireQueryPermit for /api/query requests only (see its needsPermit guard), so no client
// / SharedWorker is ever created for anything else.

import {reporter} from 'common/report/error-reporting';
import {detectBrowserAutomation} from 'iron/routes/utils/detect-browser-automation';

import {AsyncCountingSemaphorePriorityQueue} from '../async-counting-semaphore-priority-queue';
import {PoolStatsMetric, QueryPermitMetric, SoftThrottleMetric} from './metrics';
import {
  BACKGROUND,
  composeRank,
  FOREGROUND,
  getQueryPermitGlobalCap,
  MessageType,
  QUERY_PERMIT_HEARTBEAT_MS,
  QUERY_PERMIT_METRICS_FLUSH_MS,
  QUERY_PERMIT_PRIORITY_POLL_MS,
  TabToWorkerMessage,
  WorkerToTabMessage,
} from './protocol';
import {QueryPermitSoftThrottle, SoftThrottleOptions} from './soft-throttle';
import {QueryPermitClientType, QueryPermitTrackEvent} from './tracking';

const NO_OP_RELEASE = (): void => {};

function handleTrack(event: QueryPermitTrackEvent): void {
  if (typeof mixpanel === `undefined`) {
    return;
  }

  try {
    mixpanel.track(event.name, event.properties);
  } catch (error) {
    reporter.warn(error, `[Query Permit] Failed to track event ${event.name}`);
  }

  if (mixpanel.infra_metrics) {
    try {
      mixpanel.infra_metrics.track(event.name, event.properties);
    } catch (error) {
      reporter.warn(error, `[Query Permit] Failed to track event ${event.name} to infra_metrics`);
    }
  }
}

// Per-request rank inputs a caller can supply. The tab tier (foreground vs background
// tab) is managed automatically from document visibility, so it isn't part of this.
export interface PermitPriority {
  poolTier?: number;
  viewportDistance?: number;
}

function resolvePoolTier(priority?: PermitPriority): number {
  return priority?.poolTier ?? FOREGROUND;
}

function resolveViewportDistance(priority?: PermitPriority): number {
  return priority?.viewportDistance ?? 0;
}

// Canonical comparison key for a resolved priority, so the poll loop only emits an
// update-priority when the value actually changed.
function priorityKey(priority?: PermitPriority): string {
  return `${resolvePoolTier(priority)}:${resolveViewportDistance(priority)}`;
}

interface PendingAcquire {
  resolve: (release: () => void) => void;
}

interface PermitClient {
  acquire(reqId: string, projectId: number, softThrottle: boolean, priority?: PermitPriority): Promise<() => void>;
  // Re-rank a single in-flight acquire (e.g. its card scrolled toward/away from the viewport).
  updatePriority(reqId: string, priority: PermitPriority): void;
  // Withdraw an acquire whose caller gave up: drops it from the worker's queue if still
  // waiting, or releases the permit if it was granted in the race. The acquire promise
  // returned by acquire() is abandoned (never resolves) after a cancel.
  cancel(reqId: string): void;
  // Re-rank every queued request from this tab (e.g. the tab was foregrounded/backgrounded).
  // Optional: the local client is tab agnostic.
  setTabTier?(tabTier: number): void;
}

let instantiatedClient: PermitClient | undefined = undefined;

function createSharedWorkerClient(worker: SharedWorker): PermitClient {
  const port = worker.port;
  const pending: Map<string, PendingAcquire> = new Map();
  const held: Set<string> = new Set();
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  function send(msg: TabToWorkerMessage): void {
    port.postMessage(msg);
  }

  function ensureHeartbeat(): void {
    if (heartbeatTimer !== null) {
      return;
    }
    heartbeatTimer = setInterval(() => send({type: MessageType.Heartbeat}), QUERY_PERMIT_HEARTBEAT_MS);
  }

  function maybeStopHeartbeat(): void {
    if (held.size === 0 && pending.size === 0 && heartbeatTimer !== null) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
  }

  function release(reqId: string): void {
    if (!held.has(reqId)) {
      return;
    }
    held.delete(reqId);
    send({type: MessageType.Release, reqId});
    maybeStopHeartbeat();
  }

  port.addEventListener(`message`, (ev: MessageEvent<WorkerToTabMessage>) => {
    const msg = ev.data;
    if (msg.type === MessageType.Granted) {
      const waiter = pending.get(msg.reqId);
      if (!waiter) {
        // Granted after we gave up (e.g., reclaim race). Return the permit immediately.
        send({type: MessageType.Release, reqId: msg.reqId});
        return;
      }
      pending.delete(msg.reqId);
      held.add(msg.reqId);
      waiter.resolve(() => release(msg.reqId));
    } else if (msg.type === MessageType.Reclaimed) {
      // Worker took the permit back due to timeout. Drop from held so the eventual
      // release() call from the caller's finally block becomes a no-op.
      held.delete(msg.reqId);
      maybeStopHeartbeat();
    } else if (msg.type === MessageType.Track) {
      handleTrack(msg);
    }
  });
  port.start();

  const workerClient: PermitClient = {
    acquire(reqId: string, projectId: number, softThrottle: boolean, priority?: PermitPriority): Promise<() => void> {
      ensureHeartbeat();
      return new Promise<() => void>((resolve) => {
        pending.set(reqId, {resolve});
        send({
          type: MessageType.Acquire,
          reqId,
          projectId,
          softThrottle,
          poolTier: resolvePoolTier(priority),
          viewportDistance: resolveViewportDistance(priority),
        });
      });
    },
    updatePriority(reqId: string, priority: PermitPriority): void {
      send({
        type: MessageType.UpdatePriority,
        reqId,
        poolTier: resolvePoolTier(priority),
        viewportDistance: resolveViewportDistance(priority),
      });
    },
    cancel(reqId: string): void {
      if (pending.delete(reqId)) {
        // Still queued: drop the local waiter (its acquire promise is abandoned) and tell
        // the worker to give up our place in line.
        send({type: MessageType.Cancel, reqId});
        maybeStopHeartbeat();
      } else if (held.has(reqId)) {
        // Granted in the race just before we cancelled: release the permit we ended up
        // holding so it isn't pinned until the worker's reclaim timeout.
        release(reqId);
      }
    },
    setTabTier(tabTier: number): void {
      send({type: MessageType.SetTabTier, tabTier});
    },
  };

  let delegate: PermitClient = workerClient;
  worker.onerror = (): void => {
    if (delegate !== workerClient) {
      return;
    }
    if (heartbeatTimer !== null) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
    delegate = createLocalPermitClient();
    for (const waiter of pending.values()) {
      waiter.resolve(NO_OP_RELEASE);
    }
    pending.clear();
    reporter.warn(new Error(`query permit SharedWorker errored. Using tab-local fallback`), {
      reason: `sharedworker_onerror`,
    });
  };

  if (typeof window !== `undefined`) {
    window.addEventListener(`beforeunload`, () => {
      for (const reqId of Array.from(held)) {
        send({type: MessageType.Release, reqId});
      }
      // Withdraw still-queued acquires too (COR-676). beforeunload doesn't drop our
      // SharedWorker port, so a pending reqId left in the broker gets granted to this
      // dead tab and then pins its slot until the reclaim/liveness timeout (~60-90s).
      // cancel -> abortPendingKey withdraws it from the line instead.
      for (const reqId of Array.from(pending.keys())) {
        send({type: MessageType.Cancel, reqId});
      }
    });
  }

  // Tab tier follows document visibility: the viewed tab outranks backgrounded ones. Sent on connect
  // so the worker has our tier before the first visibilitychange. The local client ignores it.
  if (typeof document !== `undefined`) {
    const sendTabTier = (): void => delegate.setTabTier?.(document.hidden ? BACKGROUND : FOREGROUND);
    document.addEventListener(`visibilitychange`, sendTabTier);
    sendTabTier();
  }

  // Thin forwarder so callers always hit the current delegate (worker-backed or tab-local)
  return {
    acquire(reqId: string, projectId: number, softThrottle: boolean, priority?: PermitPriority): Promise<() => void> {
      return delegate.acquire(reqId, projectId, softThrottle, priority);
    },
    updatePriority(reqId: string, priority: PermitPriority): void {
      delegate.updatePriority(reqId, priority);
    },
    cancel(reqId: string): void {
      delegate.cancel(reqId);
    },
    setTabTier(tabTier: number): void {
      delegate.setTabTier?.(tabTier);
    },
  };
}

// ---- tab-local fallback ------------------------------------------------------

// Used when SharedWorker is unavailable (mobile web, Chrome Android, some embedded
// webviews). Enforces the per-project concurrency cap *within this tab* using the same
// AsyncCountingSemaphorePriorityQueue + composeRank ordering the worker broker uses — it
// just can't coordinate across tabs. It runs entirely on the main thread (same call stack
// as the caller), so unlike the SharedWorker broker it needs no heartbeat, reclaim, or
// liveness machinery: the caller always runs its release in a finally (even on error or
// abort), and if the tab dies the whole pool dies with it.
export function createLocalPermitClient(softThrottleOptions?: SoftThrottleOptions): PermitClient {
  const pools: Map<number, AsyncCountingSemaphorePriorityQueue> = new Map();
  // reqId -> live rank inputs for requests still waiting in line (mutated by updatePriority).
  const queued: Map<string, {projectId: number; poolTier: number; viewportDistance: number}> = new Map();
  // reqId -> project of a granted permit, so release/cancel return it to the right pool.
  const held: Map<string, number> = new Map();
  // reqIds of granted permits currently sleeping in soft-throttle backoff. If one is cancelled in
  // this phase, its reserved schedule interval is handed back so it stops delaying later grants.
  const throttling: Set<string> = new Set();
  const softThrottle = new QueryPermitSoftThrottle(softThrottleOptions);

  // Metrics
  const softThrottleMetric = new SoftThrottleMetric(QueryPermitClientType.Local);
  const poolStatsMetric = new PoolStatsMetric(QueryPermitClientType.Local);
  const metrics: QueryPermitMetric[] = [softThrottleMetric, poolStatsMetric];

  // Sample a pool's state for utilization/drain telemetry. The local pool has no dynamic cap
  // (no rescale), so cap is just its fixed max. Call wherever the pool's pending/in-flight
  // counts change (enqueue, grant, release).
  function sampleLocalPool(projectId: number): void {
    const pool = pools.get(projectId);
    if (!pool) {
      return;
    }
    const outstanding = pool.getPendingCount() + pool.getCount();
    // The local client has no concept of background or foreground tabs, so we omit those metrics.
    poolStatsMetric.recordSample(
      projectId,
      {
        pending: pool.getPendingCount(),
        inFlight: pool.getCount(),
        cap: pool.getMaxCount(),
        activePorts: outstanding > 0 ? 1 : 0,
      },
      Date.now(),
    );
  }

  function flushMetrics(): void {
    const now = Date.now();
    for (const metric of metrics) {
      for (const event of metric.drain(now)) {
        handleTrack(event);
      }
    }
  }

  // Periodic flush while the tab is active, plus a flush on hide
  setInterval(flushMetrics, QUERY_PERMIT_METRICS_FLUSH_MS);
  if (typeof document !== `undefined`) {
    document.addEventListener(`visibilitychange`, () => {
      if (document.visibilityState === `hidden`) {
        flushMetrics();
      }
    });
  }

  function getPool(projectId: number): AsyncCountingSemaphorePriorityQueue {
    let pool = pools.get(projectId);
    if (!pool) {
      pool = new AsyncCountingSemaphorePriorityQueue(getQueryPermitGlobalCap(projectId));
      pools.set(projectId, pool);
    }
    return pool;
  }

  async function handleSoftThrottle(reqId: string, projectId: number): Promise<boolean> {
    const now = Date.now();
    const {waitMs, level} = softThrottle.reserve(now);
    if (waitMs > 0) {
      throttling.add(reqId);
      await new Promise<void>((resolve) => setTimeout(resolve, waitMs));
      throttling.delete(reqId);
      if (!held.has(reqId)) {
        // Slot was cancelled during backoff
        return false;
      }
    }

    // Update Mixpanel tracking metric records
    softThrottleMetric.record(projectId, level, Date.now());
    return true;
  }

  return {
    async acquire(
      reqId: string,
      projectId: number,
      softThrottleEnabled: boolean,
      priority?: PermitPriority,
    ): Promise<() => void> {
      queued.set(reqId, {
        projectId,
        poolTier: resolvePoolTier(priority),
        viewportDistance: resolveViewportDistance(priority),
      });
      const pool = getPool(projectId);
      const enqueuedAt = Date.now();
      // The closure is re-read on every resolution, so updatePriority changes take effect
      // while the request waits in line — same live re-ranking as the worker.
      const acquisition = pool.acquire(reqId, () => {
        const rank = queued.get(reqId);
        return composeRank({
          // The local client is tab agnostic. All tabs are treated as FOREGROUND to be granted
          // max tab priority.
          tabTier: FOREGROUND,
          poolTier: rank?.poolTier ?? FOREGROUND,
          viewportDistance: rank?.viewportDistance ?? 0,
        });
      });
      sampleLocalPool(projectId);
      const granted = await acquisition;
      // Read the request's live pool tier (picks up any updatePriority) before dropping it.
      const grantedPoolTier = queued.get(reqId)?.poolTier ?? FOREGROUND;
      queued.delete(reqId);
      if (!granted) {
        // Withdrawn from the line by cancel() — no permit to release.
        return NO_OP_RELEASE;
      }

      held.set(reqId, projectId);
      sampleLocalPool(projectId);
      // Wait-for-grant is time queued for the slot; the soft-throttle backoff below is excluded
      // (SoftThrottleMetric owns that).
      // Only the pool axis is attributed: the tab axis measures cross-tab load, and this client has
      // no view of another tab to place itself in the foreground or background of.
      poolStatsMetric.recordGrant(projectId, Date.now() - enqueuedAt, {
        backgroundPool: grantedPoolTier === BACKGROUND,
      });
      if (softThrottleEnabled) {
        const grantStillValid = await handleSoftThrottle(reqId, projectId);
        if (!grantStillValid) {
          return NO_OP_RELEASE;
        }
      }
      return () => {
        if (held.delete(reqId)) {
          pool.release();
          sampleLocalPool(projectId);
        }
      };
    },
    updatePriority(reqId: string, priority: PermitPriority): void {
      const rank = queued.get(reqId);
      if (rank) {
        rank.poolTier = resolvePoolTier(priority);
        rank.viewportDistance = resolveViewportDistance(priority);
      }
    },
    cancel(reqId: string): void {
      const rank = queued.get(reqId);
      if (rank) {
        // Still queued: drop it from the line. abortPendingKey resolves the pending
        // acquire with false, so its release thunk is the no-op above.
        pools.get(rank.projectId)?.abortPendingKey(reqId);
        sampleLocalPool(rank.projectId);
        return;
      }
      const projectId = held.get(reqId);
      if (projectId !== undefined) {
        // Granted just before the cancel: release the permit we ended up holding.
        held.delete(reqId);
        if (throttling.delete(reqId)) {
          // Cancel during backoff. Hand the reserved interval back
          softThrottle.release(Date.now());
        }
        pools.get(projectId)?.release();
        sampleLocalPool(projectId);
      }
    },
  };
}

const permitClientFactory: () => PermitClient = () => {
  // A one-shot automated render (the screenshot service, headless Chrome) has no sibling tabs
  // to coordinate with, and its worker-script request never settles — which stalls the
  // `networkidle` gate the screenshot service uses to decide the report is ready to capture.
  // The tab-local client enforces the same per-project cap for the single tab, so skipping the
  // worker costs nothing. An expected path, so it is deliberately not reported.
  if (
    typeof window !== `undefined` &&
    detectBrowserAutomation({userAgent: window.navigator?.userAgent, host: window.location?.host})
  ) {
    return createLocalPermitClient();
  }
  if (typeof SharedWorker !== `undefined`) {
    try {
      // Vite's worker plugin requires literal option values; do not refactor into constants.
      const worker = new SharedWorker(new URL(`./worker.ts`, import.meta.url), {
        type: `module`,
        name: `mp-query-permits`,
      });
      return createSharedWorkerClient(worker);
    } catch (err) {
      // Construction failed (e.g. blocked in a sandboxed iframe) — fall back to tab-local.
      const error = err instanceof Error ? err : new Error(String(err));
      reporter.warn(error, {
        reason: `sharedworker_construction_failed`,
      });
    }
  }
  // No SharedWorker: enforce the cap per-tab. Cross-tab coordination is not available here.
  return createLocalPermitClient();
};

function ensureClient(): PermitClient {
  if (instantiatedClient === undefined) {
    instantiatedClient = permitClientFactory();
  }
  return instantiatedClient;
}

// Acquire a cross-tab query permit. `getPriority` is a live closure: it's read once for
// the initial rank sent with the acquire, then re-read on a shared cadence while the
// request waits in line (see the priority poll below) so a card scrolling toward/away from
// the viewport re-ranks itself without any scroll/React hook. Omit it for requests whose
// priority never changes.
//
// `abortSignal` enables early cancellation: if the caller aborts before the permit is
// granted, the queued acquire is withdrawn from the worker (it gives up its place in line)
// instead of being granted and immediately wasted on a request that will abort. The
// returned release is a no-op in that case, so the caller's release-in-finally stays valid.
export function acquireQueryPermit(
  reqId: string,
  projectId: number,
  getPriority?: () => PermitPriority,
  abortSignal?: AbortSignal | null,
  softThrottleEnabled = false,
): Promise<() => void> {
  const client = ensureClient();
  // Already aborted before enqueueing — don't take a slot in line at all.
  if (abortSignal?.aborted) {
    return Promise.resolve(NO_OP_RELEASE);
  }
  const initialPriority = getPriority?.();
  const acquirePromise = client.acquire(reqId, projectId, softThrottleEnabled, initialPriority);
  if (getPriority) {
    trackQueuedPriority(reqId, getPriority, initialPriority, acquirePromise);
  }
  if (!abortSignal) {
    return acquirePromise;
  }
  return new Promise<() => void>((resolve) => {
    const onAbort = (): void => {
      // Withdraw from the worker's queue (or release if granted in the race) and stop
      // tracking the request's live priority. The acquire promise is abandoned.
      client.cancel(reqId);
      untrackQueuedPriority(reqId);
      resolve(NO_OP_RELEASE);
    };
    abortSignal.addEventListener(`abort`, onAbort, {once: true});
    void acquirePromise.then((release) => {
      abortSignal.removeEventListener(`abort`, onAbort);
      resolve(release);
    });
  });
}

// --- live priority polling ----------------------------------------------------
//
// A single shared timer re-evaluates every queued request's live priority on each tick,
// rather than one timer per request: on a dashboard with many queued cards that's one
// wakeup per QUERY_PERMIT_PRIORITY_POLL_MS instead of one per card, and the work batches
// into a single tick. An update-priority is sent only when a request's rank actually
// changed. A request is tracked from acquire until its permit is granted (or the acquire
// otherwise settles) — once it leaves the queue its rank no longer matters.
interface QueuedPriority {
  getPriority: () => PermitPriority;
  lastKey: string;
}

const queuedPriorities: Map<string, QueuedPriority> = new Map();
let priorityPollTimer: ReturnType<typeof setInterval> | null = null;

function trackQueuedPriority(
  reqId: string,
  getPriority: () => PermitPriority,
  initialPriority: PermitPriority | undefined,
  granted: Promise<() => void>,
): void {
  queuedPriorities.set(reqId, {getPriority, lastKey: priorityKey(initialPriority)});
  if (priorityPollTimer === null) {
    priorityPollTimer = setInterval(pollQueuedPriorities, QUERY_PERMIT_PRIORITY_POLL_MS);
  }
  void granted.finally(() => untrackQueuedPriority(reqId));
}

function untrackQueuedPriority(reqId: string): void {
  queuedPriorities.delete(reqId);
  if (queuedPriorities.size === 0 && priorityPollTimer !== null) {
    clearInterval(priorityPollTimer);
    priorityPollTimer = null;
  }
}

function pollQueuedPriorities(): void {
  for (const [reqId, entry] of queuedPriorities) {
    const next = entry.getPriority();
    const nextKey = priorityKey(next);
    if (nextKey !== entry.lastKey) {
      entry.lastKey = nextKey;
      updateQueryPermitPriority(reqId, next);
    }
  }
}

// Re-rank an in-flight permit while it waits in line — e.g. a dashboard card scrolled
// nearer/further from the viewport. No-ops once the request has already been granted (it's
// no longer in line to re-rank).
export function updateQueryPermitPriority(reqId: string, priority: PermitPriority): void {
  ensureClient().updatePriority(reqId, priority);
}
